import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Table, Button, Modal, message, Space, Tag, Descriptions, List, Input, Form, Progress, Card, Row, Col, Select, DatePicker, Checkbox, Radio, Upload } from 'antd';
import { UserOutlined, SearchOutlined, PlusOutlined, DownloadOutlined, MailOutlined, EditOutlined, UserAddOutlined, MinusCircleOutlined, DeleteOutlined, UploadOutlined, CameraOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import {
  AgentDTO,
  AgentInfo,
  AgentEventInfo,
  ResponsePageAgentDTO,
  DeleteAgentRequest,
  AgentFiltersState,
  AgentsFiltersSnapshot,
  AgentEmailStateFilter,
  AgentPrintedFilter,
  AgentStatusFilter,
  AgentExportPeriodMode
} from '../types/auth';
// role checks removed; backend governs permissions
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import dayjs from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';

const { Search } = Input;
const { RangePicker } = DatePicker;

// CSS стили для анимации спиннера
const spinnerStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const AGENTS_FILTERS_STORAGE_KEY = 'agentsFilters:v1';
const AGENTS_FILTERS_VERSION = 'v1';

const DEFAULT_AGENT_FILTERS: AgentFiltersState = {
  searchQuery: '',
  emailState: 'all',
  printed: 'all',
  state: 'all'
};

const normalizeAgentFilters = (value: unknown): AgentFiltersState => {
  const candidate = (value ?? {}) as Partial<AgentFiltersState>;

  const searchQuery = typeof candidate.searchQuery === 'string' ? candidate.searchQuery : '';
  const emailState: AgentEmailStateFilter =
    candidate.emailState === 'sent' || candidate.emailState === 'not_sent' ? candidate.emailState : 'all';
  const printed: AgentPrintedFilter =
    candidate.printed === 'printed' || candidate.printed === 'not_printed' ? candidate.printed : 'all';
  const state: AgentStatusFilter =
    candidate.state === 'active' || candidate.state === 'blocked' || candidate.state === 'deleted'
      ? candidate.state
      : 'all';

  return { searchQuery, emailState, printed, state };
};

const getAgentImageSrc = (agent: Pick<AgentDTO, 'imageLink' | 'imageUrl'>): string | undefined => {
  return agent.imageLink || agent.imageUrl;
};

const isValidImageLink = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const convertFileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('Failed to read file'));
        return;
      }
      const payload = reader.result.includes('base64,')
        ? reader.result.substring(reader.result.indexOf('base64,') + 'base64,'.length)
        : reader.result;
      resolve(payload);
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

type AgentEditFormValues = {
  name: string;
  email: string;
  phone: string;
  licenseId: string;
  imageFile?: UploadFile[];
};

const Agents: React.FC = () => {
  const [agentsData, setAgentsData] = useState<ResponsePageAgentDTO>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10
  });
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [form] = Form.useForm();
  const [visitorForm] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState(DEFAULT_AGENT_FILTERS.searchQuery);
  const [emailStateFilter, setEmailStateFilter] = useState<AgentEmailStateFilter>(DEFAULT_AGENT_FILTERS.emailState);
  const [printedFilter, setPrintedFilter] = useState<AgentPrintedFilter>(DEFAULT_AGENT_FILTERS.printed);
  const [stateFilter, setStateFilter] = useState<AgentStatusFilter>(DEFAULT_AGENT_FILTERS.state);
  const [isVisitorModalVisible, setIsVisitorModalVisible] = useState(false);
  const [isAddingVisitors, setIsAddingVisitors] = useState(false);
  const [hasFormChanges, setHasFormChanges] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isExportModalVisible, setIsExportModalVisible] = useState(false);
  const [exportForm] = Form.useForm();
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [noReraCard, setNoReraCard] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({});
  const [isFiltersReady, setIsFiltersReady] = useState(false);
  const [cameraPhotoFile, setCameraPhotoFile] = useState<File | null>(null);
  const [cameraPhotoPreview, setCameraPhotoPreview] = useState<string | null>(null);
  const [pendingAgentImagePreview, setPendingAgentImagePreview] = useState<string | null>(null);
  const [isCameraCaptureVisible, setIsCameraCaptureVisible] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const skipNextPersistRef = useRef(false);
  const storageWarningShownRef = useRef(false);
  const attemptedBrowseIdRef = useRef<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const cameraVideoRef = useRef<HTMLVideoElement>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    return () => {
      if (cameraPhotoPreview) {
        URL.revokeObjectURL(cameraPhotoPreview);
      }
    };
  }, [cameraPhotoPreview]);

  useEffect(() => {
    return () => {
      if (pendingAgentImagePreview) {
        URL.revokeObjectURL(pendingAgentImagePreview);
      }
    };
  }, [pendingAgentImagePreview]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
        cameraStreamRef.current = null;
      }
    };
  }, []);

  const setBrowseQueryParam = useCallback((agentId?: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (agentId) {
        next.set('browse', agentId);
      } else {
        next.delete('browse');
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const markImageAsBroken = useCallback((imageKey: string) => {
    setBrokenImages((prev) => ({ ...prev, [imageKey]: true }));
  }, []);

  const showStorageWarning = useCallback(() => {
    if (storageWarningShownRef.current) {
      return;
    }
    storageWarningShownRef.current = true;
    message.warning('Unable to access local filter cache. Filters will work without persistence.');
  }, []);

  const persistFilters = useCallback(
    (filters: AgentFiltersState) => {
      try {
        const snapshot: AgentsFiltersSnapshot = {
          version: AGENTS_FILTERS_VERSION,
          savedAt: new Date().toISOString(),
          filters
        };
        localStorage.setItem(AGENTS_FILTERS_STORAGE_KEY, JSON.stringify(snapshot));
      } catch {
        showStorageWarning();
      }
    },
    [showStorageWarning]
  );

  const loadAgents = useCallback(async () => {
    setLoading(true);
    try {
      const normalizedQuery = searchQuery.trim();
      const data = await apiService.getAgents(
        currentPage,
        pageSize,
        normalizedQuery || undefined,
        emailStateFilter === 'all' ? undefined : emailStateFilter,
        printedFilter === 'all' ? undefined : printedFilter === 'printed',
        stateFilter === 'all' ? undefined : stateFilter
      );
      setAgentsData(data);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view agents.');
        setAgentsData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      } else {
        message.error('Failed to load agents');
        setAgentsData({
          content: [],
          totalElements: 0,
          totalPages: 0,
          currentPage: 0,
          pageSize: 10
        });
      }
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, emailStateFilter, printedFilter, stateFilter, navigate]);

  useEffect(() => {
    try {
      const snapshotRaw = localStorage.getItem(AGENTS_FILTERS_STORAGE_KEY);
      if (!snapshotRaw) {
        return;
      }

      const snapshot = JSON.parse(snapshotRaw) as Partial<AgentsFiltersSnapshot>;
      if (snapshot.version !== AGENTS_FILTERS_VERSION || !snapshot.filters) {
        return;
      }

      const normalized = normalizeAgentFilters(snapshot.filters);
      setSearchQuery(normalized.searchQuery);
      setEmailStateFilter(normalized.emailState);
      setPrintedFilter(normalized.printed);
      setStateFilter(normalized.state);
    } catch {
      showStorageWarning();
    } finally {
      setIsFiltersReady(true);
    }
  }, [showStorageWarning]);

  useEffect(() => {
    if (!isFiltersReady) {
      return;
    }
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }

    persistFilters({
      searchQuery,
      emailState: emailStateFilter,
      printed: printedFilter,
      state: stateFilter
    });
  }, [searchQuery, emailStateFilter, printedFilter, stateFilter, persistFilters, isFiltersReady]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    if (!isFiltersReady) {
      return;
    }

    loadAgents();
  }, [navigate, loadAgents, isFiltersReady]);

  // Сбрасываем страницу при изменении фильтров
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, emailStateFilter, printedFilter, stateFilter]);

  const handleViewAgent = useCallback(async (agentId: string, options?: { syncQueryParam?: boolean }) => {
    const syncQueryParam = options?.syncQueryParam ?? true;
    if (syncQueryParam) {
      setBrowseQueryParam(agentId);
    }
    setModalLoading(true);
    setIsModalVisible(true);
    try {
      const agentInfo = await apiService.getAgentInfo(agentId);
      

      
      setSelectedAgent(agentInfo);
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to view this agent.');
        setIsModalVisible(false);
        setSelectedAgent(null);
        setBrowseQueryParam(undefined);
      } else {
        message.error('Failed to load agent details');
        setIsModalVisible(false);
        setSelectedAgent(null);
        setBrowseQueryParam(undefined);
      }
    } finally {
      setModalLoading(false);
    }
  }, [navigate, setBrowseQueryParam]);

  useEffect(() => {
    if (!isAuthenticated()) {
      return;
    }

    const browseId = searchParams.get('browse')?.trim();
    if (!browseId) {
      attemptedBrowseIdRef.current = null;
      return;
    }

    if (attemptedBrowseIdRef.current === browseId) {
      return;
    }

    if (selectedAgent?.agent.id === browseId && isModalVisible) {
      attemptedBrowseIdRef.current = browseId;
      return;
    }

    attemptedBrowseIdRef.current = browseId;
    handleViewAgent(browseId, { syncQueryParam: false });
  }, [searchParams, selectedAgent, isModalVisible, handleViewAgent]);

  const handleModalClose = () => {
    if (isEditMode && hasFormChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to close without saving?',
        okText: 'Close without saving',
        cancelText: 'Continue editing',
        onOk: () => {
          stopCameraCapture();
          setIsCameraCaptureVisible(false);
          setIsModalVisible(false);
          setSelectedAgent(null);
          setIsEditMode(false);
          setIsSaving(false);
          setHasFormChanges(false);
          setCameraPhotoFile(null);
          setCameraPhotoPreview(null);
          setPendingAgentImagePreview(null);
          form.resetFields();
          setBrowseQueryParam(undefined);
        }
      });
    } else {
      stopCameraCapture();
      setIsCameraCaptureVisible(false);
      setIsModalVisible(false);
      setSelectedAgent(null);
      setIsEditMode(false);
      setIsSaving(false);
      setHasFormChanges(false);
      setCameraPhotoFile(null);
      setCameraPhotoPreview(null);
      setPendingAgentImagePreview(null);
      form.resetFields();
      setBrowseQueryParam(undefined);
    }
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleEmailStateFilterChange = (value: AgentEmailStateFilter | undefined) => {
    setEmailStateFilter(value ?? 'all');
  };

  const handlePrintedFilterChange = (value: AgentPrintedFilter | undefined) => {
    setPrintedFilter(value ?? 'all');
  };

  const handleStateFilterChange = (value: AgentStatusFilter | undefined) => {
    setStateFilter(value ?? 'all');
  };

  const handleClearFilters = () => {
    skipNextPersistRef.current = true;
    try {
      localStorage.removeItem(AGENTS_FILTERS_STORAGE_KEY);
    } catch {
      showStorageWarning();
    }

    setSearchQuery(DEFAULT_AGENT_FILTERS.searchQuery);
    setEmailStateFilter(DEFAULT_AGENT_FILTERS.emailState);
    setPrintedFilter(DEFAULT_AGENT_FILTERS.printed);
    setStateFilter(DEFAULT_AGENT_FILTERS.state);
  };

  const handleUpdatePrinted = async (agentId: string, currentPrinted: boolean) => {
    try {
      const response = await apiService.updateAgentPrinted(agentId, !currentPrinted);
      if (response.success) {
        message.success(`Agent ${!currentPrinted ? 'marked as printed' : 'marked as not printed'} successfully`);
        
        // Обновляем данные в таблице
        const updatedContent = agentsData.content.map(agent => 
          agent.id === agentId ? { ...agent, printed: !currentPrinted } : agent
        );
        setAgentsData({
          ...agentsData,
          content: updatedContent
        });
        
        // Если агент открыт в модальном окне, обновляем и его
        if (selectedAgent && selectedAgent.agent.id === agentId) {
          setSelectedAgent({
            ...selectedAgent,
            agent: { ...selectedAgent.agent, printed: !currentPrinted }
          });
        }
      } else {
        message.error(response.msg || 'Failed to update printed status');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to update this agent.');
      } else {
        message.error('Failed to update printed status');
      }
    }
  };

  const handleTableChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleAddAgent = () => {
    setIsCreateModalVisible(true);
  };

  const closeCreateModal = () => {
    setIsCreateModalVisible(false);
    setNoReraCard(false);
    setIsCreating(false);
    createForm.resetFields();
  };

  const handleCreateAgent = async (values: { name: string; email: string; phone: string; company: string; reraId?: string; imageLink: string }) => {
    const normalizedImageLink = values.imageLink.trim();
    if (!normalizedImageLink) {
      message.error('Please provide an image link');
      return;
    }
    if (!isValidImageLink(normalizedImageLink)) {
      message.error('Image link must be a valid HTTP/HTTPS URL');
      return;
    }
    if (!noReraCard && !values.reraId) {
      message.error('Enter Rera Broker Card Number or select the checkbox');
      return;
    }
    setIsCreating(true);
    try {
      const payload = {
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        company: values.company.trim(),
        reraId: noReraCard ? undefined : values.reraId?.trim(),
        imageLink: normalizedImageLink,
      };
      const resp = await apiService.addAgent(payload);
      
      // Если запрос прошел успешно (200 OK), закрываем модальное окно и перезапрашиваем список
      message.success(resp?.msg || 'Agent created successfully');
      closeCreateModal();
      loadAgents();
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('You do not have permission to create an agent');
      } else {
        const errMsg = error.response?.data?.msg || error.message || 'Failed to create agent';
        message.error(errMsg);
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleDownloadCard = (agentId: string) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'https://office.backend.ae';
    const downloadUrl = `${baseUrl}/api/v1/open/download_card?agent_id=${agentId}`;
    window.open(downloadUrl, '_blank');
  };

  const handleExportAgents = () => {
    setIsExportModalVisible(true);
  };

  const handleExportWithDates = async (values: {
    periodMode: AgentExportPeriodMode;
    dateRange?: [dayjs.Dayjs, dayjs.Dayjs];
  }) => {
    setExportLoading(true);
    try {
      const normalizedQuery = searchQuery.trim();
      let startDateStr: string | undefined;
      let endDateStr: string | undefined;

      if (values.periodMode === 'range' && values.dateRange) {
        const [startDate, endDate] = values.dateRange;
        startDateStr = startDate.format('YYYY-MM-DD');
        endDateStr = endDate.format('YYYY-MM-DD');
      }

      const response = await apiService.exportAgents({
        periodMode: values.periodMode,
        startDate: startDateStr,
        endDate: endDateStr,
        q: normalizedQuery || undefined,
        emailState: emailStateFilter === 'all' ? undefined : emailStateFilter,
        printed: printedFilter === 'all' ? undefined : printedFilter === 'printed',
        state: stateFilter === 'all' ? undefined : stateFilter,
      });
      if (response.success && response.status === 'completed' && response.body) {
        const blob = response.body;
        const fileName = response.msg || 'agents_export';
        
        // Создаем ссылку для скачивания
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        
        // Добавляем ссылку в DOM, кликаем по ней и удаляем
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Освобождаем память
        window.URL.revokeObjectURL(url);
        
        if (values.periodMode === 'range' && values.dateRange) {
          const [startDate, endDate] = values.dateRange;
          const startDateStr = startDate.format('YYYY-MM-DD');
          const endDateStr = endDate.format('YYYY-MM-DD');
          message.success(`Analytics exported successfully for period ${startDateStr} - ${endDateStr}! File: ${fileName}`);
        } else if (values.periodMode === 'all_time') {
          message.success(`Analytics exported successfully for all time! File: ${fileName}`);
        }
        setIsExportModalVisible(false);
        exportForm.resetFields();
      } else if (response.success && response.status === 'empty') {
        message.info('No agents found for selected filters and period');
        setIsExportModalVisible(false);
        exportForm.resetFields();
      } else {
        message.error(response.msg || 'Failed to export analytics');
      }
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to export analytics.');
      } else {
        message.error('Failed to export analytics');
      }
    } finally {
      setExportLoading(false);
    }
  };

  const handleResendMail = async (agentId: string) => {
    try {
      await apiService.resendMail(agentId);
      message.success('Mail resent successfully');
    } catch (error: any) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 403) {
        message.error('Access denied. You do not have permission to resend mail.');
      } else {
        message.error('Failed to resend mail');
      }
    }
  };

  const handleEditAgent = () => {
    if (selectedAgent) {
      form.setFieldsValue({
        name: selectedAgent.agent.name,
        email: selectedAgent.agent.email,
        phone: selectedAgent.agent.phone ?? '',
        licenseId: selectedAgent.agent.licenseId,
        imageFile: [],
      });
      setCameraPhotoFile(null);
      setCameraPhotoPreview(null);
      setPendingAgentImagePreview(null);
      setIsEditMode(true);
      setHasFormChanges(false);
    }
  };

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    if (!selectedAgent) return;
    
    const hasChanges = 
      allValues.name !== selectedAgent.agent.name ||
      allValues.email !== selectedAgent.agent.email ||
      (allValues.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
      allValues.licenseId !== selectedAgent.agent.licenseId ||
      (Array.isArray(allValues.imageFile) && allValues.imageFile.length > 0) ||
      cameraPhotoFile !== null;
    
    setHasFormChanges(hasChanges);
  };

  const handleCameraFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    setCameraPhotoFile(file);
    setCameraPhotoPreview(URL.createObjectURL(file));
    form.setFieldValue('imageFile', []);
    event.target.value = '';
    if (selectedAgent) {
      const values = form.getFieldsValue();
      const hasChanges =
        values.name !== selectedAgent.agent.name ||
        values.email !== selectedAgent.agent.email ||
        (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
        values.licenseId !== selectedAgent.agent.licenseId ||
        (Array.isArray(values.imageFile) && values.imageFile.length > 0) ||
        file != null;
      setHasFormChanges(hasChanges);
    }
  };

  const stopCameraCapture = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null;
    }
  };

  const handleOpenCameraCapture = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraInputRef.current?.click();
      return;
    }
    setCameraError(null);
    setIsCameraCaptureVisible(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });
      cameraStreamRef.current = stream;
      if (cameraVideoRef.current) {
        cameraVideoRef.current.srcObject = stream;
        await cameraVideoRef.current.play();
      }
    } catch (error) {
      setCameraError('Unable to access camera. Check browser permissions.');
      stopCameraCapture();
    }
  };

  const handleCloseCameraCapture = () => {
    stopCameraCapture();
    setIsCameraCaptureVisible(false);
  };

  const handleCapturePhoto = async () => {
    const video = cameraVideoRef.current;
    if (!video) return;
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      setCameraError('Camera is not ready yet.');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setCameraError('Unable to capture image.');
      return;
    }
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (!blob) {
      setCameraError('Unable to capture image.');
      return;
    }
    const file = new File([blob], `agent-camera-${Date.now()}.png`, { type: 'image/png' });
    setCameraPhotoFile(file);
    setCameraPhotoPreview(URL.createObjectURL(file));
    form.setFieldValue('imageFile', []);
    if (selectedAgent) {
      const values = form.getFieldsValue();
      const hasChanges =
        values.name !== selectedAgent.agent.name ||
        values.email !== selectedAgent.agent.email ||
        (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
        values.licenseId !== selectedAgent.agent.licenseId ||
        file != null;
      setHasFormChanges(hasChanges);
    }
    handleCloseCameraCapture();
  };

  const handleSaveAgent = async (values: AgentEditFormValues) => {
    if (!selectedAgent) return;
    const hasUploadImage = Array.isArray(values.imageFile) && values.imageFile.length > 0;
    const hasNewImage = hasUploadImage || cameraPhotoFile !== null;
    let localPreviewUrl: string | undefined;
    
    // Проверяем, были ли внесены изменения
    const hasChanges = 
      values.name !== selectedAgent.agent.name ||
      values.email !== selectedAgent.agent.email ||
      (values.phone ?? '').trim() !== (selectedAgent.agent.phone ?? '').trim() ||
      values.licenseId !== selectedAgent.agent.licenseId ||
      hasNewImage;
    
    if (!hasChanges) {
      message.info('No changes detected');
      setIsEditMode(false);
      form.resetFields();
      return;
    }
    
    setIsSaving(true);
    
    try {
      let imageBase64: string | undefined;
      if (hasNewImage) {
        const selectedFile = cameraPhotoFile ?? values.imageFile?.[0]?.originFileObj;
        if (!selectedFile) {
          message.error('Failed to read selected image');
          return;
        }
        localPreviewUrl = URL.createObjectURL(selectedFile);
        imageBase64 = await convertFileToBase64(selectedFile);
      }

      const trimmedLicenseId = (values.licenseId ?? '').trim();
      const prevLicenseId = (selectedAgent.agent.licenseId ?? '').trim();
      const trimmedPhone = (values.phone ?? '').trim();
      const prevPhone = (selectedAgent.agent.phone ?? '').trim();
      const payload: Record<string, string> = {};
      if (values.name !== selectedAgent.agent.name) {
        payload.name = values.name;
      }
      if (values.email !== selectedAgent.agent.email) {
        payload.email = values.email;
      }
      if (trimmedPhone !== prevPhone && trimmedPhone.length > 0) {
        payload.phone = trimmedPhone;
      }
      if (trimmedLicenseId !== prevLicenseId && trimmedLicenseId.length > 0) {
        payload.licenseId = trimmedLicenseId;
      }
      if (imageBase64) {
        payload.imageBase64 = imageBase64;
      }
      const response = await apiService.updateAgent(selectedAgent.agent.id, payload);
      console.log('Update response:', response);
      
      if (response.success) {
        const nextAgent = localPreviewUrl
          ? { ...response.body, imageLink: localPreviewUrl, imageUrl: localPreviewUrl }
          : response.body;
        if (localPreviewUrl) {
          if (pendingAgentImagePreview) {
            URL.revokeObjectURL(pendingAgentImagePreview);
          }
          setPendingAgentImagePreview(localPreviewUrl);
        }
        setSelectedAgent({
          ...selectedAgent,
          agent: nextAgent
        });
        setIsEditMode(false);
        setCameraPhotoFile(null);
        setCameraPhotoPreview(null);
        setHasFormChanges(false);
        form.resetFields();
        if (hasNewImage) {
          message.success('Agent updated. Photo upload to S3 has been queued.');
        } else {
          message.success(response.msg || 'Agent updated successfully');
        }
        // Обновляем только текущую страницу агентов, не перезагружая весь список
        if (agentsData.content.length > 0) {
          const updatedContent = agentsData.content.map(agent => 
            agent.id === selectedAgent.agent.id ? nextAgent : agent
          );
          setAgentsData({
            ...agentsData,
            content: updatedContent
          });
        }
      } else {
        if (localPreviewUrl) {
          URL.revokeObjectURL(localPreviewUrl);
        }
        message.error(response.msg || 'Failed to update agent');
      }
    } catch (error: any) {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl);
      }
      console.log('Update error:', error);
      console.log('Error response:', error.response);
      console.log('Error status:', error.response?.status);
      console.log('Error data:', error.response?.data);
      
      // Handle structured error responses (success=false in response body)
      if (error.response?.data && typeof error.response.data === 'object') {
        const errorData = error.response.data;
        if ('success' in errorData && 'msg' in errorData) {
          message.error(errorData.msg || 'Failed to update agent');
          return;
        }
      }
      
      // Handle other error types
      const errorMsg = error.response?.data?.msg || error.response?.data?.message || error.message || 'Failed to update agent';
      message.error(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (hasFormChanges) {
      Modal.confirm({
        title: 'Unsaved Changes',
        content: 'You have unsaved changes. Are you sure you want to cancel editing?',
        okText: 'Cancel editing',
        cancelText: 'Continue editing',
        onOk: () => {
          stopCameraCapture();
          setIsCameraCaptureVisible(false);
          setIsEditMode(false);
          setHasFormChanges(false);
          setCameraPhotoFile(null);
          setCameraPhotoPreview(null);
          form.resetFields();
        }
      });
    } else {
      stopCameraCapture();
      setIsCameraCaptureVisible(false);
      setIsEditMode(false);
      setHasFormChanges(false);
      setCameraPhotoFile(null);
      setCameraPhotoPreview(null);
      form.resetFields();
    }
  };

  const handleAgentVisited = () => {
    visitorForm.resetFields();
    visitorForm.setFieldsValue({
      visitors: [{ name: '', phone: '' }]
    });
    setIsVisitorModalVisible(true);
  };

  const handleVisitorModalClose = () => {
    setIsVisitorModalVisible(false);
    visitorForm.resetFields();
    setIsAddingVisitors(false);
  };

  const handleAddVisitors = async (values: { visitors: Array<{ name: string; phone: string }> }) => {
    if (!selectedAgent) return;
    
    setIsAddingVisitors(true);
    
    try {
      // Filter out visitors with empty names or phones
      const validVisitors = values.visitors.filter(visitor => visitor.name?.trim() || visitor.phone?.trim());
      
      const eventData = {
        guests: validVisitors
      };
      
      await apiService.addAgentEvent(selectedAgent.agent.id, eventData);
      message.success('Visitors added successfully');
      
      const updatedAgentInfo = await apiService.getAgentInfo(selectedAgent.agent.id);
      setSelectedAgent(updatedAgentInfo);
      
      setIsVisitorModalVisible(false);
      visitorForm.resetFields();
    } catch (error: any) {
      message.error('Failed to add visitors');
    } finally {
      setIsAddingVisitors(false);
    }
  };

  const handleDeleteAgent = async (agentId: string, agentName: string) => {
    // Проверяем, не удален ли уже агент
    const agent = agentsData.content.find(a => a.id === agentId);
    if (agent && agent.state === 'deleted') {
      message.error('Agent is already deleted');
      return;
    }
    
    let deleteReason = '';
    let isModalOpen = true;
    
    const deleteModal = Modal.confirm({
      title: 'Delete Confirmation',
      content: (
        <div>
          <p>Are you sure you want to delete agent "{agentName}"? This action cannot be undone.</p>
          <div style={{ marginTop: 16 }}>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 'bold', color: '#ff4d4f' }}>
              Delete Reason: *
            </label>
            <Input.TextArea
              placeholder="Enter the reason for deleting the agent..."
              rows={3}
              onChange={(e) => {
                deleteReason = e.target.value;
                // Force modal update to enable/disable OK button
                deleteModal.update({
                  okButtonProps: { 
                    disabled: !e.target.value.trim() 
                  }
                });
              }}
              style={{ width: '100%' }}
              autoFocus
            />
            <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
              Please provide a detailed reason for deleting the agent
            </div>
          </div>
        </div>
      ),
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      okButtonProps: { disabled: true }, // Button initially disabled
      onOk: async () => {
        if (!deleteReason.trim()) {
          message.error('Please enter a delete reason');
          return;
        }
        
        try {
          const deleteRequest: DeleteAgentRequest = { reason: deleteReason.trim() };
          await apiService.deleteAgent(agentId, deleteRequest.reason);
          message.success('Agent deleted successfully');
          
          // If deleting agent from modal, close it
          if (selectedAgent && selectedAgent.agent.id === agentId) {
            setIsModalVisible(false);
            setSelectedAgent(null);
            setBrowseQueryParam(undefined);
          }
          
          loadAgents(); // Reload agents to update the table
        } catch (error: any) {
          if (error.response?.status === 401) {
            navigate('/login');
          } else if (error.response?.status === 403) {
            message.error('Access denied. You do not have permission to delete this agent.');
          } else {
            message.error('Failed to delete agent');
          }
        }
      },
      onCancel: () => {
        isModalOpen = false;
      },
    });
    
    // Add modal close handler
    const handleModalClose = () => {
      if (isModalOpen) {
        isModalOpen = false;
      }
    };
    
    // Add Enter key handler
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && deleteReason.trim()) {
        // When pressing Enter with filled reason, activate OK button
        const okButton = document.querySelector('.ant-modal-confirm-btns .ant-btn-primary');
        if (okButton && !(okButton as HTMLButtonElement).disabled) {
          (okButton as HTMLButtonElement).click();
        }
      }
    };
    
    // Add Escape key handler
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleModalClose();
      }
    };
    
    document.addEventListener('keypress', handleKeyPress);
    document.addEventListener('keydown', handleKeyDown);
    
    // Clean up handlers when modal closes
    const cleanup = () => {
      document.removeEventListener('keypress', handleKeyPress);
      document.removeEventListener('keydown', handleKeyDown);
    };
    
    // Add cleanup handler when modal closes
    setTimeout(() => {
      if (!isModalOpen) {
        cleanup();
      }
    }, 100);
  };

  const handleBlockAgent = async (agentId: string, agentName: string, currentState: string) => {
    // Проверяем, не удален ли агент
    if (currentState === 'deleted') {
      message.error('Cannot perform actions on deleted agent');
      return;
    }
    
    const isCurrentlyBlocked = currentState === 'blocked';
    const action = isCurrentlyBlocked ? 'unblock' : 'block';
    
    Modal.confirm({
      title: `Confirm ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      content: `Are you sure you want to ${action} agent "${agentName}"?`,
      okText: action.charAt(0).toUpperCase() + action.slice(1),
      okType: isCurrentlyBlocked ? 'default' : 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await apiService.blockAgent(agentId, !isCurrentlyBlocked);
          message.success(`Agent ${action}ed successfully`);
          
          // Обновляем данные в таблице
          const updatedContent = agentsData.content.map(agent => 
            agent.id === agentId ? { ...agent, state: (isCurrentlyBlocked ? 'active' : 'blocked') as 'active' | 'blocked' } : agent
          );
          setAgentsData({
            ...agentsData,
            content: updatedContent
          });
          
          // Если агент открыт в модальном окне, обновляем и его
          if (selectedAgent && selectedAgent.agent.id === agentId) {
            setSelectedAgent({
              ...selectedAgent,
              agent: { ...selectedAgent.agent, state: (isCurrentlyBlocked ? 'active' : 'blocked') as 'active' | 'blocked' }
            });
          }
          
          loadAgents(); // Reload agents to update the table
        } catch (error: any) {
          if (error.response?.status === 401) {
            navigate('/login');
          } else if (error.response?.status === 403) {
            message.error('Access denied. You do not have permission to block/unblock this agent.');
          } else {
            message.error(`Failed to ${action} agent`);
          }
        }
      },
    });
  };

  const formatDateTime = (dateTime: string) => {
    return new Date(dateTime).toLocaleString();
  };

  // Вспомогательная функция для определения состояния email
  const getEmailStateInfo = (emailState: any) => {
    if (emailState === undefined || emailState === null || emailState === '') {
      return { isSent: false, label: '-', color: undefined };
    }
    
    const isSent = emailState === 'sent' || emailState === true || emailState === 'true' || emailState === 1;
    
    return {
      isSent,
      label: isSent ? 'Email Sent' : 'Email Not Sent',
      color: isSent ? 'green' : 'orange'
    };
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
      render: (id: string) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '12px',
          backgroundColor: '#f5f5f5',
          padding: '2px 6px',
          borderRadius: '4px',
          border: '1px solid #d9d9d9'
        }}>
          {id}
        </span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Photo',
      key: 'photo',
      render: (record: AgentDTO) => {
        const imageSrc = getAgentImageSrc(record);
        const imageKey = `thumb-${record.id}`;
        if (!imageSrc || brokenImages[imageKey]) return '-';
        return (
          <img 
            src={imageSrc}
            alt={record.name}
            style={{ 
              width: '40px', 
              height: '40px', 
              objectFit: 'cover',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
            onClick={() => {
              // Показываем увеличенное изображение в модальном окне
              Modal.info({
                title: `${record.name} Photo`,
                content: (
                  <div style={{ textAlign: 'center' }}>
                    <img 
                      src={imageSrc}
                      alt={record.name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '400px', 
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                    />
                  </div>
                ),
                width: 600,
                okText: 'Close'
              });
            }}
            onError={(event) => {
              event.currentTarget.style.display = 'none';
              markImageAsBroken(imageKey);
            }}
          />
        );
      },
    },
    {
      title: 'Company',
      dataIndex: 'company',
      key: 'company',
    },
    {
      title: 'License ID',
      dataIndex: 'licenseId',
      key: 'licenseId',
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => {
        const stateConfig = {
          active: { color: 'green', text: 'Active' },
          blocked: { color: 'red', text: 'Blocked' },
          deleted: { color: 'gray', text: 'Deleted' }
        };
        const config = stateConfig[state as keyof typeof stateConfig] || { color: 'default', text: state };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: AgentDTO) => (
        <Space>
          <Button 
            type="primary" 
            size="small" 
            onClick={() => handleViewAgent(record.id)}
          >
            View
          </Button>
          <Button 
            type="default" 
            size="small" 
            onClick={() => handleDownloadCard(record.id)}
          >
            Card
          </Button>

          {record.state !== 'deleted' && (
            <Button 
              type={record.state === 'blocked' ? 'default' : 'primary'}
              danger={record.state !== 'blocked'}
              size="small" 
              onClick={() => handleBlockAgent(record.id, record.name, record.state)}
            >
              {record.state === 'blocked' ? 'Unblock' : 'Block'}
            </Button>
          )}
          <Button 
            type="primary" 
            danger 
            size="small" 
            onClick={() => handleDeleteAgent(record.id, record.name)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  const selectedAgentImages = selectedAgent
    ? [
      { imageSrc: selectedAgent.agent.imageUrl || selectedAgent.agent.imageLink, badge: undefined },
      { imageSrc: selectedAgent.agent.reraImage, badge: 'Image from license' }
    ]
      .filter((entry): entry is { imageSrc: string; badge?: string } => Boolean(entry.imageSrc))
      .filter((entry, index, array) => array.findIndex((candidate) => candidate.imageSrc === entry.imageSrc) === index)
    : [];
  const selectedAgentImageEntries = selectedAgent
    ? selectedAgentImages.map(({ imageSrc, badge }, index) => ({
      imageSrc,
      badge,
      imageKey: `details-${selectedAgent.agent.id}-${index}`
    }))
    : [];
  const availableSelectedAgentImages = selectedAgent
    ? selectedAgentImageEntries.filter(({ imageKey }) => !brokenImages[imageKey])
    : [];
  const selectedAgentPrimaryImage = availableSelectedAgentImages[0];

  // always render; backend enforces permissions

  return (
    <div>
      <style>{spinnerStyles}</style>
      <div style={{ marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Agents Management</h2>

        {/* Row 1: Search */}
        <div style={{ display: 'flex' }}>
          <Search
            placeholder="Search agents by name, email, or company"
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: 420, maxWidth: '100%' }}
            onSearch={handleSearch}
          />
        </div>

        {/* Row 2: Filters + clear */}
        <div style={{ display: 'flex', gap: 16, rowGap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 170px' }}>
            <Select
              placeholder="Email State"
              size="large"
              style={{ width: 170 }}
              value={emailStateFilter}
              onChange={handleEmailStateFilterChange}
              options={[
                { value: 'all', label: 'All' },
                { value: 'sent', label: 'Email Sent' },
                { value: 'not_sent', label: 'Email Not Sent' }
              ]}
            />
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Filters by email delivery status</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 170px' }}>
            <Select
              placeholder="Printed"
              size="large"
              style={{ width: 170 }}
              value={printedFilter}
              onChange={handlePrintedFilterChange}
              options={[
                { value: 'all', label: 'All' },
                { value: 'printed', label: 'Printed' },
                { value: 'not_printed', label: 'Not Printed' }
              ]}
            />
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Filters by card print status</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 1 170px' }}>
            <Select
              placeholder="State"
              size="large"
              style={{ width: 170 }}
              value={stateFilter}
              onChange={handleStateFilterChange}
              options={[
                { value: 'all', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'blocked', label: 'Blocked' },
                { value: 'deleted', label: 'Deleted' }
              ]}
            />
            <span style={{ fontSize: 12, color: '#8c8c8c' }}>Filters by agent lifecycle state</span>
          </div>
          <Button
            size="large"
            onClick={handleClearFilters}
          >
            Clear Filters
          </Button>
        </div>

        {/* Row 3: Add left, Export right */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            size="large"
            onClick={handleAddAgent}
          >
            Add Agent
          </Button>
          <Button
            type="default"
            icon={<DownloadOutlined />}
            size="large"
            onClick={handleExportAgents}
            loading={exportLoading}
          >
            Export
          </Button>
        </div>
      </div>

      {/* Отображение активных фильтров */}
      {(searchQuery.trim() || emailStateFilter !== 'all' || printedFilter !== 'all' || stateFilter !== 'all') && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 500 }}>Active filters:</span>
          {searchQuery.trim() && (
            <Tag closable onClose={() => setSearchQuery('')}>
              Search: {searchQuery.trim()}
            </Tag>
          )}
          {emailStateFilter !== 'all' && (
            <Tag closable onClose={() => setEmailStateFilter('all')}>
              Email State: {emailStateFilter === 'sent' ? 'Email Sent' : 'Email Not Sent'}
            </Tag>
          )}
          {printedFilter !== 'all' && (
            <Tag closable onClose={() => setPrintedFilter('all')}>
              Printed: {printedFilter === 'printed' ? 'Printed' : 'Not Printed'}
            </Tag>
          )}
          {stateFilter !== 'all' && (
            <Tag closable onClose={() => setStateFilter('all')}>
              State: {stateFilter === 'active' ? 'Active' : stateFilter === 'blocked' ? 'Blocked' : 'Deleted'}
            </Tag>
          )}
        </div>
      )}

      <Table
        columns={columns}
        dataSource={agentsData.content}
        loading={loading}
        rowKey="id"
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: agentsData.totalElements,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} agents`,
          onChange: handleTableChange,
          onShowSizeChange: handleTableChange,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
      />

      <Modal
        title="Add Agent"
        open={isCreateModalVisible}
        onCancel={closeCreateModal}
        footer={[
          <Button key="cancel" onClick={closeCreateModal} disabled={isCreating}>Cancel</Button>,
          <Button key="submit" type="primary" onClick={() => createForm.submit()} loading={isCreating}>Create</Button>
        ]}
        width={700}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateAgent}
          initialValues={{ name: '', email: '', phone: '', company: '', reraId: '', imageLink: '' }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Name is required' }, { min: 2, message: 'Min length 2' }]}>
                <Input placeholder="Enter name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="Email" rules={[{ required: true, message: 'Email is required' }, { type: 'email', message: 'Invalid email' }]}>
                <Input placeholder="Enter email" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="phone" label="Phone" rules={[{ required: true, message: 'Phone is required' }]}>
                <Input placeholder="Enter phone" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="company" label="Company name" rules={[{ required: true, message: 'Company is required' }]}>
                <Input placeholder="Enter company" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item shouldUpdate noStyle>
                {() => (
                  <Form.Item name="reraId" label="Rera Broker Card Number" rules={noReraCard ? [] : [{ required: true, message: 'Rera card is required' }]}>
                    <Input placeholder={noReraCard ? "Not required" : "Enter Rera card number"} disabled={noReraCard} />
                  </Form.Item>
                )}
              </Form.Item>
              <Checkbox checked={noReraCard} onChange={(e) => setNoReraCard(e.target.checked)}>
                Don't have Rera card yet
              </Checkbox>
            </Col>
            <Col span={12}>
              {/* Image is now provided as a link instead of base64 payload */}
              <Form.Item
                name="imageLink"
                label="Image Link"
                rules={[
                  { required: true, message: 'Image link is required' },
                  { type: 'url', message: 'Enter a valid URL (http/https)' }
                ]}
              >
                <Input placeholder="https://example.com/agent-photo.jpg" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Camera Capture"
        open={isCameraCaptureVisible}
        onCancel={handleCloseCameraCapture}
        footer={[
          <Button key="cancel" onClick={handleCloseCameraCapture}>
            Cancel
          </Button>,
          <Button key="capture" type="primary" icon={<CameraOutlined />} onClick={handleCapturePhoto}>
            Capture
          </Button>
        ]}
        width={520}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <video
            ref={cameraVideoRef}
            autoPlay
            playsInline
            muted
            style={{ width: '100%', borderRadius: 8, background: '#000' }}
          />
          {cameraError && <span style={{ color: '#ff4d4f', fontSize: 12 }}>{cameraError}</span>}
        </div>
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserOutlined />
            {selectedAgent ? `Agent Details - ${selectedAgent.agent.name}` : 'Agent Details'}
            {isEditMode && (
              <Tag color="orange" style={{ marginLeft: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <EditOutlined />
                  Edit Mode
                </div>
              </Tag>
            )}
            {isSaving && (
              <Tag color="blue" style={{ marginLeft: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ 
                    width: 12, 
                    height: 12, 
                    borderRadius: '50%', 
                    border: '2px solid #1890ff', 
                    borderTopColor: 'transparent', 
                    animation: 'spin 1s linear infinite' 
                  }} />
                  Saving...
                </div>
              </Tag>
            )}
          </div>
        }
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={
          isEditMode ? [
            <Button key="reset" onClick={() => form.resetFields()} disabled={isSaving} icon={<MinusCircleOutlined />}>
              Reset
            </Button>,
            <Button key="cancel" onClick={handleCancelEdit} disabled={isSaving} icon={<MinusCircleOutlined />}>
              Cancel
            </Button>,
            <Button 
              key="save" 
              type="primary" 
              onClick={() => form.submit()}
              loading={isSaving}
              disabled={!hasFormChanges}
              icon={isSaving ? null : <EditOutlined />}
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          ] : [
            <Button key="close" onClick={handleModalClose} icon={<UserOutlined />}>
              Close
            </Button>
          ]
        }
        width={800}
        loading={modalLoading}
      >
        {selectedAgent && (
          <div>
            {isEditMode ? (
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSaveAgent}
                initialValues={{
                  name: selectedAgent.agent.name,
                  email: selectedAgent.agent.email,
                  phone: selectedAgent.agent.phone ?? '',
                  licenseId: selectedAgent.agent.licenseId,
                  imageFile: [],
                }}
                onValuesChange={handleFormValuesChange}
              >
                <Descriptions title="Agent Information (Edit Mode)" bordered column={2}>
                  <Descriptions.Item label="ID">
                    <span style={{ 
                      fontFamily: 'monospace', 
                      fontSize: '14px',
                      backgroundColor: '#f5f5f5',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid #d9d9d9'
                    }}>
                      {selectedAgent.agent.id}
                    </span>
                  </Descriptions.Item>
                  <Descriptions.Item label="Company">{selectedAgent.agent.company}</Descriptions.Item>
                  <Descriptions.Item label="Email State">
                    {selectedAgent.agent.emailState ? (
                      <Tag color={selectedAgent.agent.emailState === 'sent' ? 'green' : 'orange'}>
                        {selectedAgent.agent.emailState === 'sent' ? 'Email Sent' : 'Email Not Sent'}
                      </Tag>
                    ) : (
                      '-'
                    )}
                  </Descriptions.Item>
                  <Descriptions.Item label="Printed">
                    <Tag color={selectedAgent.agent.printed ? 'green' : 'red'}>
                      {selectedAgent.agent.printed ? 'Yes' : 'No'}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Created At">{formatDateTime(selectedAgent.agent.createdAt)}</Descriptions.Item>
                  <Descriptions.Item label="State">
                    {(() => {
                      const stateConfig = {
                        active: { color: 'green', text: 'Active' },
                        blocked: { color: 'red', text: 'Blocked' },
                        deleted: { color: 'gray', text: 'Deleted' }
                      };
                      const config = stateConfig[selectedAgent.agent.state as keyof typeof stateConfig] || { color: 'default', text: selectedAgent.agent.state };
                      return <Tag color={config.color}>{config.text}</Tag>;
                    })()}
                  </Descriptions.Item>
                </Descriptions>
                
                {hasFormChanges && (
                  <div style={{ marginBottom: 16 }}>
                    <Tag color="blue" icon={<EditOutlined />}>
                      You have unsaved changes
                    </Tag>
                    {isSaving && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <div style={{ 
                            width: 12, 
                            height: 12, 
                            borderRadius: '50%', 
                            border: '2px solid #ffa500', 
                            borderTopColor: 'transparent', 
                            animation: 'spin 1s linear infinite' 
                          }} />
                          Saving changes...
                        </div>
                      </Tag>
                    )}
                  </div>
                )}
                
                <div style={{ marginTop: 16 }}>
                  {isSaving && (
                    <div style={{ marginBottom: 16 }}>
                      <Progress 
                        percent={100} 
                        status="active" 
                        showInfo={false} 
                        strokeColor={{
                          '0%': '#108ee9',
                          '100%': '#87d068',
                        }}
                        strokeWidth={8}
                      />
                      <div style={{ textAlign: 'center', marginTop: 8, color: '#666' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                          <div style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #1890ff', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
                          Updating agent information...
                        </div>
                      </div>
                      <div style={{ textAlign: 'center', marginTop: 4, color: '#999', fontSize: '12px' }}>
                        Please wait while we save your changes
                      </div>
                    </div>
                  )}
                  
                  <Form.Item
                    label="Name"
                    name="name"
                    rules={[
                      { required: true, message: 'Name is required' },
                      { min: 2, message: 'Name must be at least 2 characters long' },
                      { max: 100, message: 'Name cannot exceed 100 characters' },
                      { pattern: /^[a-zA-Zа-яА-Я\s]+$/, message: 'Name can only contain letters and spaces' }
                    ]}
                    validateStatus={hasFormChanges && form.getFieldError('name').length > 0 ? 'error' : ''}
                    help={hasFormChanges && form.getFieldError('name').length > 0 ? form.getFieldError('name')[0] : ''}
                  >
                    <Input 
                      placeholder={isSaving ? "Saving..." : "Enter name"} 
                      disabled={isSaving}
                      suffix={isSaving ? (
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          border: '2px solid #1890ff', 
                          borderTopColor: 'transparent', 
                          animation: 'spin 1s linear infinite' 
                        }} />
                      ) : null}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Email"
                    name="email"
                    rules={[
                      { required: true, message: 'Email is required' },
                      { type: 'email', message: 'Please enter a valid email' },
                      { max: 255, message: 'Email cannot exceed 255 characters' }
                    ]}
                    validateStatus={hasFormChanges && form.getFieldError('email').length > 0 ? 'error' : ''}
                    help={hasFormChanges && form.getFieldError('email').length > 0 ? form.getFieldError('email')[0] : ''}
                  >
                    <Input 
                      placeholder={isSaving ? "Saving..." : "Enter email"} 
                      disabled={isSaving}
                      suffix={isSaving ? (
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          border: '2px solid #1890ff', 
                          borderTopColor: 'transparent', 
                          animation: 'spin 1s linear infinite' 
                        }} />
                      ) : null}
                    />
                  </Form.Item>

                  <Form.Item
                    label="Phone"
                    name="phone"
                    rules={[
                      { required: true, message: 'Phone is required' },
                      { max: 128, message: 'Phone cannot exceed 128 characters' }
                    ]}
                    validateStatus={hasFormChanges && form.getFieldError('phone').length > 0 ? 'error' : ''}
                    help={hasFormChanges && form.getFieldError('phone').length > 0 ? form.getFieldError('phone')[0] : ''}
                  >
                    <Input 
                      placeholder={isSaving ? "Saving..." : "Enter phone"} 
                      disabled={isSaving}
                      suffix={isSaving ? (
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          border: '2px solid #1890ff', 
                          borderTopColor: 'transparent', 
                          animation: 'spin 1s linear infinite' 
                        }} />
                      ) : null}
                    />
                  </Form.Item>
                  
                  <Form.Item
                    label="License ID"
                    name="licenseId"
                    rules={[
                      { max: 100, message: 'License ID cannot exceed 100 characters' }
                    ]}
                    validateStatus={hasFormChanges && form.getFieldError('licenseId').length > 0 ? 'error' : ''}
                    help={hasFormChanges && form.getFieldError('licenseId').length > 0 ? form.getFieldError('licenseId')[0] : ''}
                  >
                    <Input 
                      placeholder={isSaving ? "Saving..." : "Enter license ID"} 
                      disabled={isSaving}
                      suffix={isSaving ? (
                        <div style={{ 
                          width: 16, 
                          height: 16, 
                          borderRadius: '50%', 
                          border: '2px solid #1890ff', 
                          borderTopColor: 'transparent', 
                          animation: 'spin 1s linear infinite' 
                        }} />
                      ) : null}
                    />
                  </Form.Item>

                  <Form.Item
                    label="New Photo"
                    name="imageFile"
                    valuePropName="fileList"
                    getValueFromEvent={(event) => {
                      if (Array.isArray(event)) {
                        return event;
                      }
                      return event?.fileList;
                    }}
                    extra="Optional. If selected, the image will be uploaded asynchronously to S3 after saving."
                  >
                    <Upload
                      accept="image/*"
                      maxCount={1}
                      beforeUpload={() => false}
                      listType="picture"
                      onChange={(info) => {
                        if (info.fileList.length > 0) {
                          setCameraPhotoFile(null);
                          setCameraPhotoPreview(null);
                        }
                      }}
                    >
                      <Button icon={<UploadOutlined />} disabled={isSaving}>
                        Select Image
                      </Button>
                    </Upload>
                  </Form.Item>
                  <Form.Item label="Take Photo (Camera)">
                    <Space direction="vertical" size={8}>
                      <Button
                        icon={<CameraOutlined />}
                        onClick={handleOpenCameraCapture}
                        disabled={isSaving}
                      >
                        Open Camera
                      </Button>
                      <input
                        ref={cameraInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        style={{ display: 'none' }}
                        onChange={handleCameraFileChange}
                      />
                      {cameraPhotoPreview && (
                        <img
                          src={cameraPhotoPreview}
                          alt="Captured"
                          style={{
                            width: 180,
                            height: 180,
                            objectFit: 'cover',
                            borderRadius: 8,
                            border: '1px solid #d9d9d9'
                          }}
                        />
                      )}
                      {cameraError && (
                        <span style={{ color: '#ff4d4f', fontSize: 12 }}>{cameraError}</span>
                      )}
                    </Space>
                  </Form.Item>
                </div>

                {/* Отображение изображения агента в режиме редактирования */}
                {selectedAgentPrimaryImage && (
                  <div style={{ marginTop: 16, textAlign: 'center' }}>
                    <Card title="Agent Photo (Read-only)" size="small">
                      <img 
                        src={selectedAgentPrimaryImage.imageSrc}
                        alt={selectedAgent.agent.name}
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '300px', 
                          objectFit: 'contain',
                          borderRadius: '8px'
                        }}
                        onError={() => markImageAsBroken(selectedAgentPrimaryImage.imageKey)}
                      />
                    </Card>
                  </div>
                )}
              </Form>
            ) : (
              <div style={{ 
                display: 'flex', 
                gap: 24, 
                marginBottom: 24,
                flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                alignItems: window.innerWidth <= 768 ? 'center' : 'flex-start'
              }}>
                {/* Фотография агента */}
                <div style={{ 
                  flexShrink: 0,
                  textAlign: 'center',
                  order: window.innerWidth <= 768 ? 1 : 1
                }}>
                  {availableSelectedAgentImages.length > 0 ? (
                    <div>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 12,
                          alignItems: 'center',
                        }}
                      >
                        {availableSelectedAgentImages.map(({ imageSrc, imageKey, badge }, index) => (
                          <div key={`${imageSrc}-${index}`}>
                            <img 
                              src={imageSrc}
                              alt={`${selectedAgent.agent.name} ${index + 1}`}
                              style={{ 
                                width: window.innerWidth <= 768 ? '200px' : '250px', 
                                height: window.innerWidth <= 768 ? '200px' : '250px', 
                                objectFit: 'cover',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                border: '3px solid #d9d9d9',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                              }}
                              onClick={() => {
                                Modal.info({
                                  title: `${selectedAgent.agent.name} Photo ${index + 1}`,
                                  content: (
                                    <div style={{ textAlign: 'center' }}>
                                      <img 
                                        src={imageSrc}
                                        alt={`${selectedAgent.agent.name} ${index + 1}`}
                                        style={{ 
                                          maxWidth: '100%', 
                                          maxHeight: '600px', 
                                          objectFit: 'contain',
                                          borderRadius: '8px'
                                        }}
                                      />
                                    </div>
                                  ),
                                  width: 800,
                                  okText: 'Close'
                                });
                              }}
                              onError={() => markImageAsBroken(imageKey)}
                            />
                            {badge && (
                              <div style={{ marginTop: 6, fontSize: '12px', color: '#666', fontWeight: 500 }}>
                                {badge}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{ 
                      width: window.innerWidth <= 768 ? '200px' : '250px', 
                      height: window.innerWidth <= 768 ? '200px' : '250px',
                      border: '3px dashed #d9d9d9',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#fafafa'
                    }}>
                      <span style={{ 
                        color: '#999', 
                        fontStyle: 'italic',
                        fontSize: '16px',
                        textAlign: 'center'
                      }}>
                        No photo<br/>available
                      </span>
                    </div>
                  )}
                </div>

                {/* Информация об агенте */}
                <div style={{ 
                  flex: 1,
                  order: window.innerWidth <= 768 ? 2 : 2
                }}>
                  <Descriptions 
                    title="Agent Information" 
                    bordered 
                    column={2}
                    size="small"
                    labelStyle={{ 
                      fontWeight: 'bold',
                      padding: '8px 12px',
                      backgroundColor: '#fafafa'
                    }}
                    contentStyle={{ 
                      padding: '8px 12px'
                    }}
                  >
                    <Descriptions.Item label="Name" span={2}>
                      <span style={{ fontSize: '16px', fontWeight: '500' }}>
                        {selectedAgent.agent.name}
                      </span>
                    </Descriptions.Item>
                    {selectedAgent.agent.reraName && (
                      <Descriptions.Item label="License Name" span={2}>
                        <span style={{ fontSize: '14px' }}>
                          {selectedAgent.agent.reraName}
                        </span>
                      </Descriptions.Item>
                    )}
                    
                    <Descriptions.Item label="ID" span={2}>
                      <span style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '13px',
                        backgroundColor: '#f5f5f5',
                        padding: '3px 6px',
                        borderRadius: '3px',
                        border: '1px solid #d9d9d9',
                        display: 'inline-block',
                        width: 'fit-content'
                      }}>
                        {selectedAgent.agent.id}
                      </span>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="License" span={2}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ 
                            fontFamily: 'monospace', 
                            fontSize: '13px',
                            backgroundColor: '#e6f7ff',
                            padding: '3px 6px',
                            borderRadius: '3px',
                            border: '1px solid #91d5ff',
                            display: 'inline-block',
                            width: 'fit-content',
                            color: '#1890ff'
                          }}>
                            {selectedAgent.agent.licenseId}
                          </span>
                          {selectedAgent.agent.reraVerified !== undefined && (
                            <Tag color={selectedAgent.agent.reraVerified ? 'green' : 'red'}>
                              {selectedAgent.agent.reraVerified ? 'Verified' : 'Not Verified'}
                            </Tag>
                          )}
                        </div>
                        {selectedAgent.agent.reraVerified === false && (
                          <div style={{ color: '#ff4d4f', fontSize: '12px', fontWeight: '500' }}>
                            Hostess visit required
                          </div>
                        )}
                      </div>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Company" span={2}>
                      <span style={{ fontSize: '14px' }}>
                        {selectedAgent.agent.company}
                      </span>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Email" span={2}>
                      <span style={{ fontSize: '14px', color: '#1890ff' }}>
                        {selectedAgent.agent.email}
                      </span>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Phone" span={2}>
                      <span style={{ fontSize: '14px' }}>
                        {selectedAgent.agent.phone}
                      </span>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Registration Date" span={2}>
                      <span style={{ fontSize: '13px', color: '#666' }}>
                        {formatDateTime(selectedAgent.agent.createdAt)}
                      </span>
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Email State" span={2}>
                      {(() => {
                        const { label, color } = getEmailStateInfo(selectedAgent.agent.emailState);
                        return color ? (
                          <Tag color={color}>{label}</Tag>
                        ) : (
                          '-'
                        );
                      })()}
                    </Descriptions.Item>
                    
                    <Descriptions.Item label="Printed" span={2}>
                      <Tag color={selectedAgent.agent.printed ? 'green' : 'red'}>
                        {selectedAgent.agent.printed ? 'Yes' : 'No'}
                      </Tag>
                    </Descriptions.Item>
                    <Descriptions.Item label="State" span={2}>
                      {(() => {
                        const stateConfig = {
                          active: { color: 'green', text: 'Active' },
                          blocked: { color: 'red', text: 'Blocked' },
                          deleted: { color: 'gray', text: 'Deleted' }
                        };
                        const config = stateConfig[selectedAgent.agent.state as keyof typeof stateConfig] || { color: 'default', text: selectedAgent.agent.state };
                        return <Tag color={config.color}>{config.text}</Tag>;
                      })()}
                    </Descriptions.Item>
                  </Descriptions>
                </div>
              </div>

            )}



            {!isEditMode && (
              <div style={{ marginTop: 24 }}>
                <Card title="Quick Actions" style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={6}>
                      <Button
                        block
                        icon={<EditOutlined />}
                        onClick={handleEditAgent}
                      >
                        Edit
                      </Button>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Button
                        block
                        icon={<MailOutlined />}
                        onClick={() => handleResendMail(selectedAgent.agent.id)}
                      >
                        Resend Email
                      </Button>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Button
                        block
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadCard(selectedAgent.agent.id)}
                      >
                        Download Card
                      </Button>
                    </Col>
                    <Col xs={24} sm={6}>
                      <Button
                        block
                        type="primary"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDeleteAgent(selectedAgent.agent.id, selectedAgent.agent.name)}
                      >
                        Delete
                      </Button>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                    <Col xs={24} sm={12}>
                      <Button
                        block
                        type={selectedAgent.agent.printed ? 'default' : 'primary'}
                        onClick={() => handleUpdatePrinted(selectedAgent.agent.id, selectedAgent.agent.printed)}
                      >
                        {selectedAgent.agent.printed ? 'Mark as Not Printed' : 'Mark as Printed'}
                      </Button>
                    </Col>
                    {selectedAgent.agent.state !== 'deleted' && (
                      <Col xs={24} sm={12}>
                        <Button
                          block
                          type={selectedAgent.agent.state === 'blocked' ? 'default' : 'primary'}
                          danger={selectedAgent.agent.state !== 'blocked'}
                          onClick={() => handleBlockAgent(selectedAgent.agent.id, selectedAgent.agent.name, selectedAgent.agent.state)}
                        >
                          {selectedAgent.agent.state === 'blocked' ? 'Unblock Agent' : 'Block Agent'}
                        </Button>
                      </Col>
                    )}
                  </Row>
                </Card>
                
                {/* Agent Status Block */}
                {(selectedAgent.agent.state === 'deleted' || selectedAgent.agent.state === 'blocked') && (
                  <Card 
                    style={{ 
                      marginTop: 16, 
                      marginBottom: 16,
                      borderColor: '#ff4d4f',
                      backgroundColor: '#fff2f0'
                    }}
                    size="small"
                  >
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 12,
                      color: '#ff4d4f'
                    }}>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: 'bold' 
                      }}>
                        {selectedAgent.agent.state === 'deleted' ? 'Agent Deleted' : 'Agent Blocked'}
                      </div>
                      {selectedAgent.agent.blockReason && (
                        <div style={{ 
                          fontSize: '14px',
                          color: '#666',
                          fontStyle: 'italic'
                        }}>
                          Reason: {selectedAgent.agent.blockReason}
                        </div>
                      )}
                    </div>
                  </Card>
                )}
                
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <h3>Events History ({selectedAgent.events.length})</h3>

                  {selectedAgent.agent.state === 'blocked' || selectedAgent.agent.state === 'deleted' ? (
                    <div style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: '16px' }}>
                      {selectedAgent.agent.state === 'blocked' ? 'Agent Blocked' : 'Agent Deleted'}
                    </div>
                  ) : (
                    <Button
                      type="primary"
                      icon={<UserAddOutlined />}
                      onClick={handleAgentVisited}
                      disabled={selectedAgent.agent.reraVerified === false}
                    >
                      Agent Visited
                    </Button>
                  )}
                </div>
                <List
                  dataSource={selectedAgent.events}
                  renderItem={(event: AgentEventInfo) => (
                    <List.Item>
                      <List.Item.Meta
                        title={
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Tag color="blue">{event.event.toUpperCase()}</Tag>
                            <span>{formatDateTime(event.dateTime)}</span>
                          </div>
                        }
                        description={
                          event.amount && event.amount > 0
                            ? `${event.merchantName ? `Merchant: ${event.merchantName} | ` : ''}Amount AED: ${event.amount}`
                            : (event.merchantName ? `Merchant: ${event.merchantName}` : 'No merchant information')
                        }
                      />
                    </List.Item>
                  )}
                  locale={{
                    emptyText: 'No events recorded for this agent'
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <UserAddOutlined />
            Agent Visited
          </div>
        }
        open={isVisitorModalVisible}
        onCancel={handleVisitorModalClose}
        footer={[
          <Button key="cancel" onClick={handleVisitorModalClose} disabled={isAddingVisitors}>
            Cancel
          </Button>,
          <Button 
            key="submit" 
            type="primary" 
            onClick={() => visitorForm.submit()}
            loading={isAddingVisitors}
          >
            Add Visitors
          </Button>
        ]}
        width={600}
      >
        <Form
          form={visitorForm}
          layout="vertical"
          onFinish={handleAddVisitors}
          initialValues={{
            visitors: [{ name: '', phone: '' }]
          }}
        >
          <Form.List name="visitors">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card 
                    key={key} 
                    size="small" 
                    title={`Visitor ${name + 1}`}
                    style={{ marginBottom: 16 }}
                    extra={
                      fields.length > 1 ? (
                        <Button
                          type="text"
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                          disabled={isAddingVisitors}
                        />
                      ) : null
                    }
                  >
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'name']}
                          label="Name (Optional)"
                        >
                          <Input placeholder="Enter visitor name (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'phone']}
                          label="Phone (Optional)"
                        >
                          <Input placeholder="Enter phone number (optional)" disabled={isAddingVisitors} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    block
                    icon={<PlusOutlined />}
                    disabled={isAddingVisitors}
                  >
                    Add Another Visitor
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form>
      </Modal>

      <Modal
        title="Analytics Export"
        open={isExportModalVisible}
        onCancel={() => {
          setIsExportModalVisible(false);
          exportForm.resetFields();
        }}
        footer={[
          <Button 
            key="cancel" 
            onClick={() => {
              setIsExportModalVisible(false);
              exportForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button 
            key="export" 
            type="primary" 
            onClick={() => exportForm.submit()}
            loading={exportLoading}
          >
            Export
          </Button>
        ]}
        width={600}
      >
        <Form
          form={exportForm}
          layout="vertical"
          onFinish={handleExportWithDates}
        >
          <Form.Item
            label="Export Options"
            help="Choose how you want to filter the export"
          >
            <Form.Item
              name="periodMode"
              initialValue="range"
            >
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                style={{ width: '100%' }}
                options={[
                  { value: 'range', label: 'Date Range' },
                  { value: 'all_time', label: 'All Time' }
                ]}
                onChange={(value) => {
                  const selectedMode = value?.target?.value ?? value;
                  if (selectedMode === 'all_time') {
                    exportForm.setFieldsValue({ dateRange: undefined });
                  }
                }}
              />
            </Form.Item>
          </Form.Item>
          
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.periodMode !== currentValues.periodMode}
          >
            {({ getFieldValue }) => {
              const periodMode = getFieldValue('periodMode');

              if (periodMode === 'range') {
                return (
                  <Form.Item
                    label="Date Range"
                    name="dateRange"
                    rules={[{ required: true, message: 'Please select a date range' }]}
                  >
                    <RangePicker 
                      style={{ width: '100%' }}
                      placeholder={['Start Date', 'End Date']}
                    />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Agents;